import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react-native";
import {
  ActivityIndicator,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  type PressableProps,
  type StyleProp,
  type TextStyle,
  type ViewStyle,
} from "react-native";
import { BlurView } from "expo-blur";
import { semanticColors } from "../theme/tokens";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

export type CompactActionButtonSize = "md" | "sm";
export type CompactActionButtonVariant = "primary" | "secondary" | "selected";

export type CompactActionButtonProps = Omit<
  PressableProps,
  "children" | "style"
> & {
  label: string;
  size?: CompactActionButtonSize;
  /** Stable analytics identifier, required when `label` is translated. */
  action?: string;
  variant?: CompactActionButtonVariant;
  loading?: boolean;
  leading?: ReactNode;
  leadingIcon?: LucideIcon;
  icon?: ReactNode;
  trailing?: ReactNode;
  trailingIcon?: LucideIcon;
  iconSize?: number;
  iconStrokeWidth?: number;
  flexGrow?: number;
  flexBasis?: number;
  minWidth?: number;
  minHeight?: number;
  disabledOpacity?: number;
  pressedOpacity?: number;
  activityIndicatorColor?: string;
  numberOfLines?: number;
  style?: StyleProp<ViewStyle>;
  textStyle?: StyleProp<TextStyle>;
};

const SIZE_STYLE: Record<
  CompactActionButtonSize,
  {
    minHeight: number;
    paddingVertical: number;
    paddingHorizontal: number;
    gap: number;
    fontSize: number;
    lineHeight?: number;
  }
> = {
  md: {
    minHeight: 38,
    paddingVertical: 10,
    paddingHorizontal: 13,
    gap: 6,
    fontSize: 12.5,
    lineHeight: 16,
  },
  sm: {
    minHeight: 34,
    paddingVertical: 7,
    paddingHorizontal: 11,
    gap: 6,
    fontSize: 12,
  },
};

const VARIANT_STYLE: Record<
  CompactActionButtonVariant,
  {
    backgroundColor: string;
    pressedBackgroundColor: string;
    borderColor: string;
    textColor: string;
    activityIndicatorColor: string;
  }
> = {
  /** Tonal: a soft grey-brown tint with no visible outline. */
  primary: {
    backgroundColor: semanticColors.action.tonal.background,
    pressedBackgroundColor: semanticColors.action.tonal.pressedBackground,
    borderColor: "transparent",
    textColor: semanticColors.action.tonal.foreground,
    activityIndicatorColor: semanticColors.action.tonal.foreground,
  },
  selected: {
    backgroundColor: "rgba(42, 37, 34, 0.1)",
    pressedBackgroundColor: "rgba(42, 37, 34, 0.14)",
    borderColor: "rgba(42, 37, 34, 0.24)",
    textColor: semanticColors.text.primary,
    activityIndicatorColor: semanticColors.text.primary,
  },
  /**
   * Frosted glass: translucent white over a blur, with a white rim and a lit
   * top edge, so it reads as a live button on light glass cards. The rim and
   * blur come from FROSTED_SECONDARY_STYLE and the native BlurView below.
   */
  secondary: {
    backgroundColor: "rgba(255, 255, 255, 0.46)",
    pressedBackgroundColor: "rgba(255, 255, 255, 0.3)",
    borderColor: "transparent",
    textColor: semanticColors.text.primary,
    activityIndicatorColor: semanticColors.text.primary,
  },
};

/** The secondary's white rim, lit top edge and soft lift, plus blur on web. */
const FROSTED_SECONDARY_STYLE: ViewStyle = {
  overflow: "hidden",
  boxShadow:
    "inset 0px 0px 0px 1px rgba(255, 255, 255, 0.75), inset 0px 1px 0px rgba(255, 255, 255, 0.95), 0px 1px 3px rgba(42, 37, 34, 0.06)",
  ...(Platform.OS === "web"
    ? ({
        backdropFilter: "blur(14px) saturate(1.5)",
        WebkitBackdropFilter: "blur(14px) saturate(1.5)",
      } as ViewStyle)
    : null),
};

/**
 * The compact secondary's frosted surface, for pills that show an action
 * inside a larger tappable row and so are not buttons themselves.
 */
export const compactSecondarySurfaceStyle: ViewStyle = {
  backgroundColor: VARIANT_STYLE.secondary.backgroundColor,
  ...FROSTED_SECONDARY_STYLE,
};

export function CompactActionButton({
  label,
  onPress,
  size = "md",
  variant = "primary",
  loading = false,
  leading,
  leadingIcon: LeadingIcon,
  icon,
  trailing,
  trailingIcon: TrailingIcon,
  action,
  iconSize = 13,
  iconStrokeWidth = 1.9,
  flexGrow,
  flexBasis,
  minWidth,
  minHeight,
  disabled = false,
  disabledOpacity = 0.58,
  pressedOpacity = 0.78,
  activityIndicatorColor,
  accessibilityLabel,
  numberOfLines,
  style,
  textStyle,
  ...pressableProps
}: CompactActionButtonProps) {
  const sizeStyle = SIZE_STYLE[size];
  const variantStyle = VARIANT_STYLE[variant];
  const isInactive = disabled || loading;
  const iconColor = variantStyle.textColor;
  const capturePress = usePressCapture("CompactActionButton");

  return (
    <Pressable
      {...pressableProps}
      onPress={(event) => {
        capturePress(pressLabel(label, accessibilityLabel), action);
        onPress?.(event);
      }}
      accessibilityLabel={accessibilityLabel ?? label}
      accessibilityRole="button"
      accessibilityState={{ disabled: isInactive, busy: loading }}
      disabled={isInactive}
      style={({ pressed }) => [
        {
          minHeight: minHeight ?? sizeStyle.minHeight,
          ...(flexGrow != null ? { flexGrow } : null),
          ...(flexBasis != null ? { flexBasis } : null),
          ...(minWidth != null ? { minWidth } : null),
          borderRadius: 999,
          borderWidth: 1,
          borderColor: variantStyle.borderColor,
          backgroundColor: pressed
            ? variantStyle.pressedBackgroundColor
            : variantStyle.backgroundColor,
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          gap: sizeStyle.gap,
          paddingHorizontal: sizeStyle.paddingHorizontal,
          paddingVertical: sizeStyle.paddingVertical,
          opacity: loading
            ? 0.7
            : disabled
              ? disabledOpacity
              : pressed
                ? pressedOpacity
                : 1,
        },
        variant === "secondary" ? FROSTED_SECONDARY_STYLE : null,
        style,
      ]}
    >
      {variant === "secondary" && Platform.OS !== "web" ? (
        <BlurView
          pointerEvents="none"
          intensity={24}
          tint="light"
          experimentalBlurMethod="dimezisBlurView"
          style={StyleSheet.absoluteFill}
        />
      ) : null}
      {loading ? (
        <ActivityIndicator
          size="small"
          color={activityIndicatorColor ?? variantStyle.activityIndicatorColor}
        />
      ) : (
        (leading ??
        icon ??
        (LeadingIcon ? (
          <LeadingIcon
            size={iconSize}
            color={iconColor}
            strokeWidth={iconStrokeWidth}
          />
        ) : null))
      )}
      <Text
        className="font-dm-sans"
        numberOfLines={numberOfLines}
        style={[
          {
            fontSize: sizeStyle.fontSize,
            lineHeight: sizeStyle.lineHeight,
            color: variantStyle.textColor,
            fontWeight: "800",
            textAlign: "center",
          },
          textStyle,
        ]}
      >
        {label}
      </Text>
      {trailing ??
        (TrailingIcon ? (
          <TrailingIcon
            size={iconSize}
            color={iconColor}
            strokeWidth={iconStrokeWidth}
          />
        ) : null)}
    </Pressable>
  );
}

export default CompactActionButton;
